import { IconButton, List, Stack } from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import { useEffect, useState, useCallback, useMemo } from "react";

import { InviteCode, ListItemCustom } from "./UI";
import { useSidebarContext } from "./context/SidebarStateContext";
import { CopyIcon } from "components/icon";
import { isValidValue } from "utils";
import { useMenuSelectionContext } from "./context/MenuSelectionContext";
import { specialIndex } from "./constant";
import { useTranslation } from "react-i18next";

/**
 * SidebarMenuList Component
 * -------------------------
 * Displays sidebar menu items.
 * Supports:
 *  - Mini sidebar (collapsed state)
 *  - Filter bar (show only selected item)
 *  - Navigation on click
 *  - Invite code section
 *
 * Props:
 * - filterBar (boolean) → if true, filters the menu list
 */
const SidebarMenuList = ({ filterBar = false }) => {
  const { t } = useTranslation("layout");
  // STEP 1: Get global state from contexts
  const { selectedIndex, setSelectedIndex } = useMenuSelectionContext();
  const { listItems, isMainSidebar, showSidebar } = useSidebarContext();

  // STEP 2: Setup routing utilities
  const navigate = useNavigate();
  const location = useLocation();

  // STEP 3: Local state for filtered list items
  const [mainListItemsFilter, setMainListItemsFilter] = useState(listItems);

  // STEP 4: Determine mini sidebar mode
  const miniSB = useMemo(() => (!showSidebar && !filterBar), [showSidebar, filterBar]);

  /**
   * STEP 5: Update selected index when menu item clicked
   * - Toggle if clicked again
   * - Reset after delay when id=null (mini sidebar closing)
   */
  const setIndex = useCallback((id = null) => {
    if (!!id) {
      setSelectedIndex((prevState) => (prevState === id ? null : id));
    } else {
      setTimeout(() => setSelectedIndex(null), 100);
    }
  }, [setSelectedIndex]);

  /**
   * STEP 6: Special handler for invite code button in mini sidebar
   * - Toggles between null and specialIndex
   */
  const setIndex1 = useCallback(() => {
    if (!filterBar && miniSB) {
      setSelectedIndex((prevState) => (isValidValue(prevState) ? null : specialIndex));
    }
  }, [miniSB, filterBar, setSelectedIndex]);

  /**
   * STEP 7: Handle navigation when menu item clicked
   * - Main sidebar → navigate directly
   * - District sidebar → prefix with /districts/:id
   * - Always reset index in mini sidebar
   */
  const buttonClick = useCallback(
    (path, id = null) => {
      if (isMainSidebar) {
        navigate(path);
      } else {
        navigate(`${path}`);
      }
      setIndex(null);
    },
    [navigate,setIndex, isMainSidebar]
  );

  /**
   * STEP 8: Effect for filterBar behavior
   * - If selectedIndex === specialIndex → auto-detect index from URL
   * - Else → filter to only show selected item
   */
  useEffect(() => {
    if (!filterBar) return;

    if (selectedIndex === specialIndex) {
      const selectedItem = listItems.find((item) =>
        location.pathname.includes(item.check)
      );
      if (selectedItem) {
        setIndex(selectedItem.id);
      } else {
        setIndex(listItems[0].id);
      }
    } else {
      setMainListItemsFilter(listItems.filter((el) => el.id === selectedIndex));
    }
  }, [filterBar, selectedIndex, location.pathname, listItems, setIndex]);

  /**
   * STEP 9: Keep filtered list in sync with listItems
   */
  useEffect(() => {
    setMainListItemsFilter(listItems);
  }, [listItems]);

  // STEP 10: Memoize invite code UI
  const inviteCode = useMemo(() => <InviteCode />, []);

  // STEP 11: Memoize copy button UI
  const copyButton = useMemo(() => (
    <IconButton
      sx={{ alignSelf: "center", fontSize: 20 }}
      onClick={setIndex1}
      aria-label={t("copyInviteCode")}
    >
      <CopyIcon fontSize="inherit" />
    </IconButton>
  ), [t, setIndex1]);

  /**
   * STEP 12: Render
   * - Show filtered/normal menu items
   * - Show Invite Code (depends on sidebar mode)
   */
  return (
    <Stack
      sx={{
        flexGrow: 1,
        p: 1,
        justifyContent: "space-between",
        position: "relative",
        overflow: 'hidden'
      }}
    >
      {/* Menu Items */}
      <List dense role="menu" aria-label={t("mainMenu")} sx={{ overflow:'scroll' }}>
        {mainListItemsFilter.map((item) => (
          <ListItemCustom
            key={item.id}
            item={item}
            setIndex={setIndex}
            miniSB={miniSB}
            filterBar={filterBar}
            buttonClick={buttonClick}
          />
        ))}
      </List>

      {/* Invite Code */}
      {!isMainSidebar &&
        (miniSB ? copyButton : inviteCode)
      }
    </Stack>
  );
};

export default SidebarMenuList;
